/**
 * Klaviyo Back-in-Stock button placement & labeling.
 *
 * Klaviyo injects #klaviyo-bis-button-container at the bottom of the document
 * after its widget loads. We want it to sit next to the Sold out submit
 * button, and we want the label to read "Notify me" (the default is a long
 * "NOTIFY ME WHEN BACK IN STOCK" string that gets clipped by our 50/50
 * layout).
 *
 * This script:
 *   - Runs once globally (loaded via <script defer> from the main product
 *     section).
 *   - Watches the document for the Klaviyo container to appear or for the
 *     button inside it to be (re-)rendered.
 *   - Re-runs placement on PUB_SUB_EVENTS.variantChange and on
 *     'product-info:loaded' (fired after swap-product viewTransitions), so a
 *     section/product swap doesn't leave the Klaviyo button orphaned.
 *   - Pins the label: if Klaviyo overwrites the button's text after we set
 *     it, our textContent observer flips it back.
 *
 * The button's sold-out state and the Klaviyo container's visibility are
 * driven entirely by product-form.js (toggleSubmitButton) plus the CSS
 * :has(.product-form__submit--soldout) rules in section-main-product.css —
 * this script does not touch them.
 */
(function () {
  // The script tag sits inside <main>, which a colour swap replaces and
  // re-executes. Everything below is document-level, so a second run would
  // only stack another observer and another click listener.
  if (window.__weldunBisPlacementLoaded) return;
  window.__weldunBisPlacementLoaded = true;

  /*
    Open Klaviyo's back-in-stock form from the theme's own trigger.

    Klaviyo injects its own button once its dynamic button handler sees a
    sold-out variant, but that can take a few seconds after load. The theme
    renders this trigger for variants that are sold out at render time so
    there is something to press meanwhile; the CSS hides it once Klaviyo's
    container lands beside it. openForm() opens the published form, which
    klaviyo-bis-drawer.js then wraps in the drawer.

    Delegated from the document because the product section re-renders on
    variant change, replacing the button.
  */
  var BIS_READY_TIMEOUT = 12000;
  var BIS_POLL_INTERVAL = 150;

  function klaviyoReady() {
    return !!(window.klaviyo && typeof window.klaviyo.openForm === 'function');
  }

  function openWhenReady(trigger, formId, waitedFor) {
    if (klaviyoReady()) {
      trigger.classList.remove('is-loading');
      trigger.removeAttribute('aria-busy');
      window.klaviyo.openForm(formId);
      return;
    }

    if (waitedFor >= BIS_READY_TIMEOUT) {
      // Give the control back rather than leaving it stuck looking busy.
      trigger.classList.remove('is-loading');
      trigger.removeAttribute('aria-busy');
      return;
    }

    setTimeout(function () {
      openWhenReady(trigger, formId, waitedFor + BIS_POLL_INTERVAL);
    }, BIS_POLL_INTERVAL);
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest && event.target.closest('.klaviyo-bis-trigger');
    if (!trigger) return;

    var formId = trigger.dataset.klaviyoBisForm;
    if (!formId) return;

    event.preventDefault();

    /*
      Klaviyo's onsite bundle can take several seconds to expose openForm, and
      the button is visible long before that. Swallowing an early click looked
      exactly like a broken button, so wait for Klaviyo instead and mark the
      trigger busy while we do.
    */
    if (!klaviyoReady()) {
      trigger.classList.add('is-loading');
      trigger.setAttribute('aria-busy', 'true');
    }

    openWhenReady(trigger, formId, 0);
  });

  var KLAVIYO_LABEL = 'Notify me';
  // Must not match the theme's own .klaviyo-bis-trigger: with both it and
  // Klaviyo's container in the same row, each pass moved one in front of the
  // other, and the document observer re-ran on every move - an endless loop
  // that froze the page whenever a sold-out variant rendered with Klaviyo's
  // button present.
  var KLAVIYO_BTN_SELECTOR =
    '#klaviyo-bis-button-container button, [class*="klaviyo-bis"]:not(.klaviyo-bis-trigger) button, button[class*="klaviyo-bis"]:not(.klaviyo-bis-trigger)';
  var labelObservers = new WeakMap();

  function applyLabel(kbtn) {
    if (!kbtn) return;
    // If the visible text already matches, leave it.
    if ((kbtn.textContent || '').trim() === KLAVIYO_LABEL) return;
    kbtn.textContent = '';
    var span = document.createElement('span');
    span.textContent = KLAVIYO_LABEL;
    kbtn.appendChild(span);
  }

  function pinLabel(kbtn) {
    applyLabel(kbtn);
    if (labelObservers.has(kbtn)) return;
    var obs = new MutationObserver(function () {
      // Klaviyo may re-render the button content; flip it back without
      // entering an infinite loop (applyLabel is a no-op when text matches).
      applyLabel(kbtn);
    });
    obs.observe(kbtn, { childList: true, subtree: true, characterData: true });
    labelObservers.set(kbtn, obs);
  }

  function placeNextToSubmit(kbtn) {
    // Walk up from the klaviyo button to find its outermost wrapper that we
    // can move as a single unit (the container Klaviyo injected).
    var wrapper = kbtn;
    while (
      wrapper.parentNode &&
      wrapper.parentNode !== document.body &&
      !(wrapper.parentNode.classList && wrapper.parentNode.classList.contains('product-form__buttons'))
    ) {
      wrapper = wrapper.parentNode;
    }
    // If we walked all the way to body, the container hasn't been placed
    // inside a product form yet — find the first visible submit button and
    // move it there.
    var targetSubmit = null;
    if (wrapper.parentNode === document.body || !wrapper.parentNode) {
      targetSubmit = document.querySelector('[id^="ProductSubmitButton-"]');
      if (!targetSubmit) return;
      var formButtons = targetSubmit.parentNode;
      if (formButtons && wrapper.parentNode !== formButtons) {
        formButtons.insertBefore(wrapper, targetSubmit.nextSibling);
      }
      return;
    }
    // Already inside a product-form__buttons — make sure it's the one
    // immediately after that form's submit button.
    var formButtons = wrapper.parentNode;
    var submit = formButtons.querySelector('[id^="ProductSubmitButton-"]');
    if (submit && submit.nextSibling !== wrapper) {
      formButtons.insertBefore(wrapper, submit.nextSibling);
    }
  }

  function runPlacement() {
    var buttons = document.querySelectorAll(KLAVIYO_BTN_SELECTOR);
    if (!buttons.length) return false;
    buttons.forEach(function (kbtn) {
      placeNextToSubmit(kbtn);
      pinLabel(kbtn);
    });
    return true;
  }

  // Watch the whole document for the Klaviyo widget to appear or its
  // button to be re-rendered. Scoping to a specific container doesn't
  // work because Klaviyo can replace the container itself.
  var bodyObs = new MutationObserver(function () {
    runPlacement();
  });
  bodyObs.observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  // Re-run on variant changes (the section-render path keeps the submit
  // button in place, but a swap-product viewTransition will replace it,
  // leaving the Klaviyo wrapper orphaned outside the new form).
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, function () {
      runPlacement();
    });
  }
  document.addEventListener('product-info:loaded', function () {
    runPlacement();
  });

  // Initial pass once the DOM is ready.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runPlacement);
  } else {
    runPlacement();
  }
})();
